/**
 * FBA 费用计算逻辑
 */
import { Site, ProductType } from './types';
import { getFBAConfig } from './config';

/**
 * 判断是否为服装类目
 */
const isApparelCategory = (category: string): boolean => {
    const apparelKeywords = [
        '服装', 'apparel', 'clothing', 'fashion', 'wear',
        '鞋', 'shoes', 'footwear',
        '包', 'bags', 'handbags',
        '首饰', 'jewelry', 'accessories'
    ];
    const lowerCategory = category.toLowerCase();
    return apparelKeywords.some(keyword => lowerCategory.includes(keyword));
};

/**
 * 计算FBA配送费
 * @param site 站点
 * @param length cm
 * @param width cm
 * @param height cm
 * @param weight kg
 * @param price 售价 (用于判断低价费率)
 * @param productType 产品类型
 * @param isPeakSeason 是否旺季 (10月-1月)
 * @param hasLithiumBattery 是否包含锂电池
 * @param calculationDate 计算日期（用于选择费率版本）
 * @param category 商品类目（用于判断是否服装）
 */
export const calculateFBAFee = (
    site: Site,
    length: number,
    width: number,
    height: number,
    weight: number,
    price: number,
    productType: ProductType = 'standard',
    isPeakSeason: boolean = false,
    hasLithiumBattery: boolean = false,
    calculationDate: Date = new Date(),
    category?: string
): {
    fee: number;
    tierName: string;
    isLowPrice: boolean;
    lithiumBatteryFee: number;
    totalFee: number;
} | null => {
    // 使用getFBAConfig根据日期获取正确的费率配置
    const config = getFBAConfig(site, calculationDate);
    if (!config) return null;

    // 1. 判断是否符合低价费率
    const isLowPrice = price > 0 && price <= config.lowPriceThreshold && productType === 'standard';

    // 2. 根据品类判断是否为服装
    const isApparel = category ? isApparelCategory(category) : productType === 'apparel';

    // 3. 选择费率表（按优先级）
    let tiers = config.sizeTiers.standard;
    let selectedTierType = 'standard';

    // 优先级1: 高峰期独立费率表（2026+）
    if (isPeakSeason && config.sizeTiers.peak && config.sizeTiers.peak.length > 0) {
        tiers = config.sizeTiers.peak;
        selectedTierType = 'peak';
    }
    // 优先级2: 危险品
    else if (productType === 'dangerous_goods' && config.sizeTiers.dangerous) {
        tiers = config.sizeTiers.dangerous;
        selectedTierType = 'dangerous';
    }
    // 优先级3-4: 服装类
    else if (isApparel) {
        if (isLowPrice && config.sizeTiers.apparelLowPrice && config.sizeTiers.apparelLowPrice.length > 0) {
            tiers = config.sizeTiers.apparelLowPrice;
            selectedTierType = 'apparelLowPrice';
        } else if (config.sizeTiers.apparel) {
            tiers = config.sizeTiers.apparel;
            selectedTierType = 'apparel';
        }
    }
    // 优先级5: 标准商品低价
    else if (isLowPrice && config.sizeTiers.lowPrice.length > 0) {
        tiers = config.sizeTiers.lowPrice;
        selectedTierType = 'lowPrice';
    }

    // 4. 排序尺寸边长 (长 >= 宽 >= 高)
    const edges = [length, width, height].sort((a, b) => b - a);

    // 5. 匹配最便宜的符合条件的Tier
    for (const tier of tiers) {
        // 检查重量
        if (weight > tier.maxWeight) continue;

        // 检查尺寸
        if (edges[0] > tier.maxEdge[0] || edges[1] > tier.maxEdge[1] || edges[2] > tier.maxEdge[2]) {
            continue;
        }

        let fee = tier.fee;
        let tierName = tier.tierName;

        // 6. 应用旧式旺季附加费（仅用于没有独立peak费率表的情况）
        if (isPeakSeason && config.peakSurcharge && selectedTierType !== 'peak') {
            const isOversize = /Oversize|Large Bulky|Extra Large/.test(tierName);
            const surcharge = isOversize ? config.peakSurcharge.oversize : config.peakSurcharge.standard;
            if (surcharge > 0) {
                fee += surcharge;
                tierName += ` (旺季 +${config.currency}${surcharge})`;
            }
        }

        // 7. 计算锂电池费用
        const lithiumFee = hasLithiumBattery && config.lithiumBatteryFee ? config.lithiumBatteryFee : 0;

        return {
            fee: parseFloat(fee.toFixed(2)),
            tierName: tierName,
            isLowPrice: selectedTierType === 'lowPrice' || selectedTierType === 'apparelLowPrice',
            lithiumBatteryFee: lithiumFee,
            totalFee: parseFloat((fee + lithiumFee).toFixed(2))
        };
    }

    // 如果没有匹配到（比如超大超重）
    if (tiers.length > 0) {
        const lastTier = tiers[tiers.length - 1];
        const lithiumFee = hasLithiumBattery && config.lithiumBatteryFee ? config.lithiumBatteryFee : 0;
        return {
            fee: lastTier.fee,
            tierName: `${lastTier.tierName} (或超标)`,
            isLowPrice: false,
            lithiumBatteryFee: lithiumFee,
            totalFee: parseFloat((lastTier.fee + lithiumFee).toFixed(2))
        };
    }

    return null;
};

/**
 * 计算月度仓储费
 * @param site 站点
 * @param length cm
 * @param width cm
 * @param height cm
 * @param tierName 尺寸分段名称 (用于判断是否大件)
 * @param calculationDate 计算日期（用于选择费率版本）
 */
export const calculateStorageFee = (
    site: Site,
    length: number,
    width: number,
    height: number,
    tierName: string,
    calculationDate: Date = new Date()
): { jan_sep: number; oct_dec: number } => {
    const config = getFBAConfig(site, calculationDate);
    if (!config || !config.storageFees) return { jan_sep: 0, oct_dec: 0 };

    // 1. 计算体积
    // cu_ft = (cm * cm * cm) / 28316.8
    // cbm = (cm * cm * cm) / 1000000
    const volumeCm3 = length * width * height;

    // 2. 判断是否大件 (简单逻辑：如果tierName包含 Large, Oversize 等)
    const isOversize = /Oversize|Large Bulky|Extra Large/.test(tierName);
    const feeConfig = isOversize ? config.storageFees.oversize : config.storageFees.standard;

    let volume = 0;
    if (feeConfig.unit === 'cu_ft') {
        volume = volumeCm3 / 28316.8466;
    } else {
        volume = volumeCm3 / 1000000;
    }

    return {
        jan_sep: parseFloat((volume * feeConfig.jan_sep).toFixed(2)),
        oct_dec: parseFloat((volume * feeConfig.oct_dec).toFixed(2))
    };
};
